import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, Info, ChevronRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import type { EoTheme, EoExercise, EoProgress } from "@/types/eoTypes";
import { eoThemes, eoExercises } from "@/data/eoMockData";

export default function EoVocabPlayer() {
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useNavigate();
  
  const [theme, setTheme] = useState<EoTheme | null>(null);
  const [exercises, setExercises] = useState<EoExercise[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [isFinished, setIsFinished] = useState(false);
  const [score, setScore] = useState(0);

  useEffect(() => {
    const foundTheme = eoThemes.find(t => t.id === themeId);
    if (foundTheme) {
      setTheme(foundTheme);
      const themeExercises = eoExercises.filter(e => e.themeId === themeId);
      setExercises(themeExercises);
    }
  }, [themeId]);

  if (!theme || exercises.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p className="text-slate-500 mb-4">Tema não encontrado ou sem exercícios.</p>
        <Button onClick={() => navigate("/grammar")} variant="outline">Voltar</Button>
      </div>
    );
  }

  const currentExercise = exercises[currentIndex];
  const progressPercent = Math.round((currentIndex / exercises.length) * 100);

  const handleOptionClick = (option: string) => {
    if (selectedOption !== null) return; // Prevent multiple clicks
    
    setSelectedOption(option);
    const correct = option === currentExercise.correctOption;
    setIsCorrect(correct);
    if (correct) {
      setScore(prev => prev + 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < exercises.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedOption(null);
      setIsCorrect(null);
    } else {
      setIsFinished(true);
      saveProgress();
    }
  };

  const saveProgress = () => {
    try {
      const stored = localStorage.getItem("tef_eo_progress");
      let progressData: Record<string, EoProgress> = stored ? JSON.parse(stored) : {};
      
      const themeProgress = progressData[themeId!] || { themeId: themeId!, completedExercises: [] };
      
      // For this mock version, we just save all exercise IDs if they finished the session
      exercises.forEach(ex => {
        if (!themeProgress.completedExercises.includes(ex.id)) {
          themeProgress.completedExercises.push(ex.id);
        }
      });
      
      progressData[themeId!] = themeProgress;
      localStorage.setItem("tef_eo_progress", JSON.stringify(progressData));
    } catch (err) {
      console.error("Failed to save progress", err);
    }
  };

  if (isFinished) {
    return (
      <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in duration-500 pb-12 mt-12 text-center">
        <div className="w-24 h-24 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-12 h-12" />
        </div>
        <h1 className="text-4xl font-black text-slate-800 tracking-tight">C'est fini !</h1>
        <p className="text-lg text-slate-500 max-w-lg mx-auto">
          Você completou o treino do tema <span className="font-bold text-slate-700">"{theme.title}"</span>.
          <br/>Sua pontuação: <span className="font-bold text-indigo-600">{score} / {exercises.length}</span>.
        </p>
        <div className="pt-8">
          <Button 
            onClick={() => navigate("/grammar")}
            className="rounded-xl px-8 py-6 text-lg font-bold bg-indigo-600 hover:bg-indigo-700"
          >
            Voltar para Gramática
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-in fade-in duration-500 pb-24 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate("/grammar")}
          className="text-slate-500 hover:text-slate-800 -ml-2 rounded-xl"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div className="text-sm font-bold text-slate-400">
          Exercício {currentIndex + 1} de {exercises.length}
        </div>
      </div>

      <Progress value={progressPercent} className="h-2 mb-10 bg-slate-100 [&>div]:bg-indigo-600" />

      {/* Context Area */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-bold uppercase tracking-wide mb-4">
          Contexto
        </div>
        <p className="text-slate-500 text-sm italic border-l-4 border-indigo-200 pl-4 py-1">
          {currentExercise.context}
        </p>
      </div>

      {/* Phrase Area */}
      <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8 md:p-12 text-center mb-8 min-h-[200px] flex items-center justify-center">
        <h2 className="text-2xl md:text-3xl font-serif text-slate-800 leading-relaxed">
          {currentExercise.textBeforeBlank}
          <span className={`inline-block min-w-[120px] px-4 py-1 mx-2 rounded-xl border-b-4 transition-all duration-300 ${
            selectedOption === null ? 'border-slate-300 bg-slate-50' : 
            isCorrect ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-bold' : 
            'border-rose-500 bg-rose-50 text-rose-700 font-bold'
          }`}>
            {selectedOption || "..."}
          </span>
          {currentExercise.textAfterBlank}
        </h2>
      </div>

      {/* Options Area */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {currentExercise.options.map((option, idx) => {
          let btnState = "default";
          if (selectedOption !== null) {
            if (option === currentExercise.correctOption) {
              btnState = "correct";
            } else if (option === selectedOption) {
              btnState = "incorrect";
            } else {
              btnState = "disabled";
            }
          }

          return (
            <Button
              key={idx}
              onClick={() => handleOptionClick(option)}
              disabled={selectedOption !== null}
              className={`h-16 text-lg rounded-2xl font-bold transition-all shadow-sm ${
                btnState === "default" ? "bg-white text-slate-700 border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-700" :
                btnState === "correct" ? "bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600" :
                btnState === "incorrect" ? "bg-rose-50 text-rose-500 border-rose-200 line-through opacity-80" :
                "bg-slate-50 text-slate-400 border-slate-100 opacity-50"
              }`}
            >
              {option}
              {btnState === "correct" && <CheckCircle className="w-5 h-5 ml-2" />}
              {btnState === "incorrect" && <XCircle className="w-5 h-5 ml-2" />}
            </Button>
          );
        })}
      </div>

      {/* Explanation Area */}
      {selectedOption !== null && (
        <div className="mt-8 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <div className={`p-6 rounded-2xl border ${isCorrect ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
            <div className="flex items-start gap-3">
              <div className={`shrink-0 p-2 rounded-full ${isCorrect ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                {isCorrect ? <CheckCircle className="w-6 h-6" /> : <XCircle className="w-6 h-6" />}
              </div>
              <div className="flex-1">
                <h3 className={`font-bold text-lg mb-1 ${isCorrect ? 'text-emerald-800' : 'text-rose-800'}`}>
                  {isCorrect ? "Trés bien !" : "Oups !"}
                </h3>
                <p className={`text-sm ${isCorrect ? 'text-emerald-700/80' : 'text-rose-700/80'}`}>
                  {currentExercise.explanation}
                </p>
              </div>
              <Button 
                onClick={handleNext}
                className={`shrink-0 rounded-xl font-bold shadow-sm ${isCorrect ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}`}
              >
                {currentIndex < exercises.length - 1 ? "Próximo" : "Finalizar"}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
